import { withConsentPilot } from "../utils/consent";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  API_URL,
  REDIRECT_LINK,
  STRIPE_PUBLIC_KEY,
} from "../constants/formConstants";
import { getUpsellV2Offer, getUpsellV2Target } from "../utils/upsellV2Flow";
import { upsellV2Session } from "../utils/upsellV2Session";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../utils/checkoutMetadata";
import { getAcquisitionCountryMetadataFromSearch } from "../utils/acquisitionCountry";
let stripePromise;
const defaultGetStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
  return stripePromise;
};
const defaultNavigate = (target) => window.location.replace(withConsentPilot(target));
const intentId = (value) =>
  typeof value === "string" && /^pi_[A-Za-z0-9]+$/.test(value);
const isCurrentAttempt = (store, attempt) => {
  const session = store.resume();
  return (
    session?.id === attempt.chainId &&
    session.currentStep === attempt.stepIndex &&
    session.steps[attempt.stepIndex].attemptId === attempt.attemptId &&
    session.steps[attempt.stepIndex].outcome === "submitted"
  );
};

// Mount once per route (or pass the changing pathname). Navigation always replaces
// completed offers. Injected adapters allow contract/browser tests without charges.
export function useUpsellV2Payment({
  pathname,
  store = upsellV2Session,
  navigate = defaultNavigate,
  redirectLink = REDIRECT_LINK,
  post = axios.post,
  getStripe = defaultGetStripe,
  onComplete,
  onState,
}) {
  const [session, setSession] = useState(null);
  const context = useRef(null);
  const callbacks = useRef({
    navigate,
    onComplete,
    onState,
  });
  callbacks.current = {
    navigate,
    onComplete,
    onState,
  };
  const lastNavigation = useRef(null);
  useEffect(() => {
    const chainId = store.read()?.id;
    const binding = {
      chainId,
      pathname,
      session: null,
      departure: 0,
    };
    context.current = binding;
    const refresh = () => {
      if (binding.hidden) return;
      const next = store.read();
      // A new main purchase owns its navigation, never the old request/page.
      if (next && next.id !== chainId) {
        binding.session = null;
        setSession(null);
        return;
      }
      binding.session = next;
      setSession(next);
      let reporting;
      try {
        reporting = callbacks.current.onState?.({ session: next, pathname });
        Promise.resolve(reporting).catch(() => {});
      } catch (_) {}
      const target = getUpsellV2Target(next, redirectLink, window.location.search);
      const productKey = next?.steps[next.currentStep]?.productKey;
      if (getUpsellV2Offer(productKey, next?.currency)?.route === pathname)
        return;
      const navigationKey = `${chainId}:${pathname}:${target}`;
      if (lastNavigation.current !== navigationKey) {
        lastNavigation.current = navigationKey;
        const departure = binding.departure;
        const proceed = () => {
          const latest = store.read();
          if (
            binding.hidden || binding.departure !== departure || context.current !== binding ||
            (latest && latest.id !== chainId) ||
            getUpsellV2Target(latest, redirectLink, window.location.search) !== target
          )
            return;
          callbacks.current.navigate(target, { replace: true });
        };
        proceed();
      }
    };
    // Back/Forward cache preserves promises and refs without React unmounting.
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
    const leave = () => {
      binding.hidden = true;
      binding.departure += 1;
      lastNavigation.current = null;
      const current = store.read();
      const step = current?.steps[current.currentStep];
      if (current?.id === chainId && step?.outcome === "submitted") {
        store.finish({ chainId, stepIndex: current.currentStep, attemptId: step.attemptId }, "unknown");
      }
    };
    const resume = (event) => {
      if (!event.persisted) return;
      binding.hidden = false;
      refresh();
    };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", resume);
    store.resume();
    const unsubscribe = store.subscribe(refresh);
    refresh();
    return () => {
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", resume);
      unsubscribe();
      if (context.current === binding) context.current = null;
    };
  }, [pathname, store, redirectLink]);
  const actionable = (allowSubmitted = false) => {
    const binding = context.current;
    const current = binding?.session;
    const step = current?.steps[current.currentStep];
    if (
      binding?.hidden || binding?.pathname !== pathname ||
      !(step?.outcome === "ready" || (allowSubmitted && step?.outcome === "submitted")) ||
      getUpsellV2Offer(step.productKey, current.currency)?.route !== pathname
    )
      return null;
    return {
      binding,
      current,
      step,
      expected: {
        chainId: current.id,
        stepIndex: current.currentStep,
      },
    };
  };
  const report = (action, outcome, paymentIntentId) => {
    if (context.current !== action.binding) return;
    // Analytics must never prevent the next offer or cause a second submission.
    try {
      const result = callbacks.current.onComplete?.({
        ...action.expected,
        productKey: action.step.productKey,
        currency: action.current.currency,
        outcome,
        ...(paymentIntentId
          ? {
              paymentIntentId,
            }
          : {}),
      });
      Promise.resolve(result).catch(() => {});
    } catch (_) {}
  };
  const add = async () => {
    const action = actionable();
    if (!action) return;
    const attempt = store.begin(action.expected);
    if (!attempt) return;
    let outcome = "unknown";
    let paymentIntentId;
    try {
      const credentials = store.credentials();
      if (!credentials) {
        outcome = "failed";
      } else {
        const offer = getUpsellV2Offer(
          action.step.productKey,
          action.current.currency,
        );
        const { data } = await post(
          `${API_URL}/upsell-payment`,
          {
            offerId: offer.offerId,
            ...credentials,
            data: {
              ...getUtmMetadataFromSearch(action.current.search, {
                maxKeys: 40,
              }),
              ...getAcquisitionCountryMetadataFromSearch(action.current.search),
              currency: action.current.currency,
              client_reference_id: action.current.clientReferenceId,
              paywall: action.current.initialPaywall,
            },
          },
          {
            headers: {
              "Idempotency-Key": `upsell-v2-${attempt.attemptId}`,
            },
            timeout: 30000,
          },
        );
        if (!isCurrentAttempt(store, attempt)) return;
        if (
          data?.success === true &&
          intentId(data.id) &&
          !data.requires_action &&
          (data.status === undefined || data.status === "succeeded")
        ) {
          outcome = "succeeded";
          paymentIntentId = data.id;
        } else if (data?.requires_action === true) {
          if (
            intentId(data.payment_intent_id) &&
            typeof data.client_secret === "string" &&
            data.client_secret.startsWith(`${data.payment_intent_id}_secret_`)
          ) {
            // A removed page must not open a bank challenge over another screen.
            // https://react.dev/reference/react/useEffect#fetching-data-with-effects
            if (context.current !== action.binding) {
              store.finish(attempt, "unknown");
              return;
            }
            const stripe = await getStripe();
            if (!isCurrentAttempt(store, attempt)) return;
            if (context.current !== action.binding) {
              store.finish(attempt, "unknown");
              return;
            }
            if (stripe?.confirmCardPayment) {
              const confirmed = await stripe.confirmCardPayment(
                data.client_secret,
              );
              if (confirmed?.error) outcome = "failed";
              else if (
                confirmed?.paymentIntent?.id === data.payment_intent_id
              ) {
                paymentIntentId = confirmed.paymentIntent.id;
                if (confirmed.paymentIntent.status === "succeeded")
                  outcome = "succeeded";
                else if (
                  ["canceled", "requires_payment_method"].includes(
                    confirmed.paymentIntent.status,
                  )
                )
                  outcome = "failed";
              }
            } else outcome = "failed";
          }
        } else if (
          data?.success === false &&
          !["processing", "requires_capture"].includes(data.status)
        )
          outcome = "failed";
      }
    } catch (_) {
      // A timeout/network/SDK exception cannot prove a failed charge. Never retry.
      outcome = "unknown";
    }
    if (store.finish(attempt, outcome, paymentIntentId))
      report(action, outcome, paymentIntentId);
  };
  const skip = () => {
    const action = actionable(true);
    if (action && store.skip(action.expected)) report(action, "skipped");
  };
  const step = session?.steps[session.currentStep];
  const offer = getUpsellV2Offer(step?.productKey, session?.currency);
  const onCurrentRoute = offer?.route === pathname;
  return {
    add,
    skip,
    session,
    offer: onCurrentRoute ? offer : null,
    loading: onCurrentRoute && step?.outcome === "submitted",
    disabled: !onCurrentRoute || step?.outcome !== "ready",
    skipDisabled: !onCurrentRoute || !["ready", "submitted"].includes(step?.outcome),
  };
}
